import { useCallback, useEffect, useRef, useState } from "react";
import type { PublicContractCancellationRevocationResolveResponseDto } from "@madamuh/domain";
import {
  confirmPublicContractCancellationRevocation,
  resolvePublicContractCancellationRevocation
} from "../../shared/api/commercialContract";

function formatDateTime(value: string | null | undefined): string {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("ja-JP", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  }).format(date);
}

function restoredStatusLabel(status: "trialing" | "active"): string {
  return status === "trialing" ? "無料期間中" : "契約中";
}

function consumeTokenFromFragment(): string {
  const token = new URLSearchParams(window.location.hash.replace(/^#/, "")).get("token")?.trim() ?? "";
  if (window.location.hash) {
    window.history.replaceState(null, document.title, window.location.pathname);
  }
  return token;
}

export function ContractCancellationRevocationActionPage() {
  const tokenRef = useRef("");
  const resolveInFlightRef = useRef(false);
  const [resolved, setResolved] = useState<PublicContractCancellationRevocationResolveResponseDto | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [isResolving, setIsResolving] = useState(false);

  const resolveToken = useCallback(async (rawToken: string) => {
    if (!rawToken) {
      setResolved(null);
      setMessage("確認リンクに必要な情報がありません。");
      return;
    }
    if (resolveInFlightRef.current) return;

    resolveInFlightRef.current = true;
    setIsResolving(true);
    try {
      const response = await resolvePublicContractCancellationRevocation(rawToken);
      setResolved(response);
      setMessage(
        response.status === "windowClosed"
          ? "解約予約取消期限を過ぎているため、このリンクでは取消しできません。"
          : null
      );
    } catch {
      setMessage("確認リンクを読み込めませんでした。");
    } finally {
      resolveInFlightRef.current = false;
      setIsResolving(false);
    }
  }, []);

  const consumeAndResolveFragment = useCallback(() => {
    const token = consumeTokenFromFragment();
    if (!token) return;
    tokenRef.current = token;
    void resolveToken(token);
  }, [resolveToken]);

  const retryCurrentToken = useCallback(() => {
    if (!tokenRef.current) {
      setMessage("確認リンクに必要な情報がありません。");
      return;
    }
    void resolveToken(tokenRef.current);
  }, [resolveToken]);

  useEffect(() => {
    consumeAndResolveFragment();
  }, [consumeAndResolveFragment]);

  useEffect(() => {
    window.addEventListener("hashchange", consumeAndResolveFragment);
    window.addEventListener("pageshow", consumeAndResolveFragment);
    return () => {
      window.removeEventListener("hashchange", consumeAndResolveFragment);
      window.removeEventListener("pageshow", consumeAndResolveFragment);
    };
  }, [consumeAndResolveFragment]);

  async function confirm() {
    if (!tokenRef.current || !resolved?.confirmationFingerprint) return;

    try {
      const completed = await confirmPublicContractCancellationRevocation(
        tokenRef.current,
        resolved.confirmationFingerprint
      );
      setMessage(
        `解約予約を取り消しました。契約は継続されます。復元後: ${restoredStatusLabel(
          completed.restoredContractStatus
        )}`
      );
    } catch {
      setMessage("取消し条件が更新されました。最新内容を確認して、もう一度確定してください。");
    }
  }

  return (
    <main style={{ maxWidth: 720, margin: "0 auto", padding: 16 }}>
      <section style={{ display: "grid", gap: 12, border: "1px solid #e5e7eb", borderRadius: 16, padding: 20 }}>
        <h1>解約予約の取消し確認</h1>
        {message ? <p>{message}</p> : null}
        {resolved?.preview ? (
          <>
            <p>利用終了予定日: {formatDateTime(resolved.preview.serviceEndsAt)}</p>
            <p>最初に利用しない月: {resolved.preview.firstSkippedServiceMonth}</p>
            <p>取消期限: {formatDateTime(resolved.preview.cancellationRevocableUntil)}</p>
            <p>復元後の契約状態: {restoredStatusLabel(resolved.preview.restoredContractStatus)}</p>
            <p>取消しを確定すると、現在設定されている利用終了予定は解除されます。将来の利用および請求が再開される場合があります。</p>
            <p>{resolved.preview.billingRestorationSummary.message}</p>
            <button type="button" disabled={isResolving || (resolved.status !== "valid" && resolved.status !== "previewChanged")} onClick={() => void confirm()}>
              解約予約の取消しを確定する
            </button>
          </>
        ) : null}
        <button type="button" disabled={isResolving} onClick={retryCurrentToken}>
          {isResolving ? "読み込み中..." : "もう一度読み込む"}
        </button>
      </section>
    </main>
  );
}
